import { useQuery, useQueryClient } from '@tanstack/react-query';
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, type ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ApiError, api, setCsrfToken } from '../api/client';
import { clearSignInContinuation, takeSignInContinuation } from '../lib/continuation';
import { keys } from '../api/queries';
import type { Actor, Attribution, MeResponse, Role } from '../api/types';
import { ErrorState, Loading } from '../components/States';
import { SignIn } from '../routes/SignIn';

const rank: Record<Role, number> = { member: 0, steward: 1, owner: 2 };

export interface Session {
  user: Actor;
  attribution: Attribution | null;
  /** True when the signed-in role is at least `role`. */
  hasRole: (role: Role) => boolean;
  signOut: () => Promise<void>;
}

const SessionContext = createContext<Session | null>(null);

export function useSession(): Session {
  const session = useContext(SessionContext);
  if (session === null) throw new Error('useSession must be used inside <SessionGate>');
  return session;
}

async function loadMe(signal: AbortSignal): Promise<MeResponse> {
  const me = await api.get<MeResponse>('/api/me', signal);
  setCsrfToken(me.csrfToken);
  return me;
}

/**
 * Every hub view requires an invited, signed-in Discord account. There is no
 * anonymous or demo mode: a 401 renders the sign-in screen and nothing else.
 */
export function SessionGate({ children }: { children: ReactNode }) {
  const client = useQueryClient();
  const me = useQuery({
    queryKey: keys.me,
    queryFn: ({ signal }) => loadMe(signal),
    retry: (count, error) => !(error instanceof ApiError && (error.isUnauthenticated || error.isForbidden)) && count < 2,
    staleTime: 60_000,
  });

  const signOut = useCallback(async () => {
    try {
      await api.post('/auth/logout', {});
    } finally {
      clearSignInContinuation();
      setCsrfToken(null);
      client.clear();
      window.location.assign('/');
    }
  }, [client]);

  const session = useMemo<Session | null>(() => {
    if (me.data === undefined) return null;
    const { user, preferences } = me.data;
    return {
      user,
      attribution: preferences?.attribution ?? null,
      hasRole: (role) => rank[user.role] >= rank[role],
      signOut,
    };
  }, [me.data, signOut]);

  if (me.isPending) return <Loading label="Checking your session" fullPage />;
  if (me.error instanceof ApiError && me.error.isUnauthenticated) return <SignIn />;
  if (me.error instanceof ApiError && me.error.isForbidden) return <SignIn notInvited />;
  if (me.error !== null || session === null) {
    return (
      <main className="page page--narrow" id="main">
        <ErrorState error={me.error} onRetry={() => void me.refetch()} title="We could not confirm your session" />
      </main>
    );
  }

  return (
    <SessionContext.Provider value={session}>
      <SignInContinuation />
      {children}
    </SessionContext.Provider>
  );
}

/**
 * Once a real session exists, consumes the one-shot sign-in note (lib/continuation):
 * a fresh note from the new-project page returns there from the post-sign-in landing page.
 */
function SignInContinuation() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const consumed = useRef(false);
  useEffect(() => {
    if (consumed.current) return;
    consumed.current = true;
    const target = takeSignInContinuation();
    if (target !== null && pathname === '/') void navigate(target, { replace: true });
  }, [navigate, pathname]);
  return null;
}

/** Renders children only for the given role or higher; otherwise explains why. */
export function RequireRole({ role, children }: { role: Role; children: ReactNode }) {
  const session = useSession();
  if (session.hasRole(role)) return <>{children}</>;
  return (
    <main className="page page--narrow" id="main" tabIndex={-1}>
      <div className="state state--forbidden" role="status">
        <p className="eyebrow">Restricted</p>
        <h1 className="state__title">This area is for {role === 'owner' ? 'the owner' : 'stewards'}</h1>
        <p>
          Your account is signed in as a {session.user.role}. Stewardship tools review findings, compose exports and
          watch collection and budget state. You can still read, explore and challenge findings.
        </p>
      </div>
    </main>
  );
}
